import { useState, useRef } from "react";
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Checkbox,
    FormControlLabel,
    Collapse,
    TextField,
    Select,
    MenuItem,
    FormControl,
    CircularProgress,
    LinearProgress
} from "@mui/material";
import {
    Delete as DeleteIcon,
    ContentCopy as CopyIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Add as AddIcon,
    Close as CloseIcon,
    UploadFile as UploadFileIcon,
    InsertDriveFile as FileIcon,
    PictureAsPdf as PdfIcon,
    DragIndicator as DragIcon,
    Error as ErrorIcon
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { QUESTION_TYPES, QUESTION_TYPE_KEYS } from "../../enums/QuestionTypes";
import { uploadPdf } from "../../services/MediaService";

// Get validation error message based on question type
const getValidationErrorMessage = (type) => {
    switch (type) {
        case QUESTION_TYPE_KEYS.LINK:
            return 'חובה למלא כתובת URL';
        case QUESTION_TYPE_KEYS.PDF_ATTACHMENT:
            return 'חובה להעלות קובץ PDF';
        case QUESTION_TYPE_KEYS.MULTIPLE_CHOICE:
            return 'חובה להוסיף לפחות 2 אפשרויות';
        default:
            return 'חובה למלא שדה זה';
    }
};

const QuestionCard = ({
    question,
    onUpdate,
    onDelete,
    onDuplicate,
    onDragStart,
    onDragEnd,
    hasError = false
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const cardRef = useRef(null);

    const isHeaderType = question.type === QUESTION_TYPE_KEYS.FORM_TITLE ||
        question.type === QUESTION_TYPE_KEYS.SECTION_HEADER ||
        question.type === QUESTION_TYPE_KEYS.FREE_TEXT;

    const handleDragStartWithImage = (e) => {
        // Set the entire card as the drag image with correct offset for RTL
        if (cardRef.current) {
            const cardRect = cardRef.current.getBoundingClientRect();
            const offsetX = cardRect.right - e.clientX;
            const offsetY = e.clientY - cardRect.top;
            e.dataTransfer.setDragImage(cardRef.current, cardRect.width - offsetX, offsetY);
        }
        if (onDragStart) {
            onDragStart(e);
        }
    };

    const handleAddOption = () => {
        const newOptions = [...(question.options || []), `אפשרות ${(question.options?.length || 0) + 1}`];
        onUpdate({ options: newOptions });
    };

    const handleUpdateOption = (optionIndex, value) => {
        const newOptions = [...(question.options || [])];
        newOptions[optionIndex] = value;
        onUpdate({ options: newOptions });
    };

    const handleDeleteOption = (optionIndex) => {
        const newOptions = question.options.filter((_, i) => i !== optionIndex);
        onUpdate({ options: newOptions });
    };

    const renderQuestionEditor = () => {
        switch (question.type) {
            case QUESTION_TYPE_KEYS.FORM_TITLE:
                return (
                    <TextField
                        fullWidth
                        value={question.title || ''}
                        onChange={(e) => onUpdate({ title: e.target.value })}
                        placeholder="כותרת הטופס הראשית"
                        variant="standard"
                        InputProps={{
                            disableUnderline: true,
                            sx: {
                                fontSize: "24px",
                                fontWeight: 600,
                                textAlign: "center",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }
                        }}
                        sx={{
                            "& input": { textAlign: "center" }
                        }}
                    />
                );

            case QUESTION_TYPE_KEYS.SECTION_HEADER:
                return (
                    <TextField
                        fullWidth
                        value={question.title || ''}
                        onChange={(e) => onUpdate({ title: e.target.value })}
                        placeholder="כותרת המקטע"
                        variant="standard"
                        InputProps={{
                            disableUnderline: true,
                            sx: {
                                fontSize: "20px",
                                fontWeight: 600,
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }
                        }}
                    />
                );

            case QUESTION_TYPE_KEYS.FREE_TEXT:
                return (
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        value={question.content || ''}
                        onChange={(e) => onUpdate({ content: e.target.value })}
                        placeholder="הזן טקסט חופשי להסבר או הנחיות..."
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }
                        }}
                    />
                );

            case QUESTION_TYPE_KEYS.MULTIPLE_CHOICE:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="טקסט השאלה"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                            sx={{ marginBottom: "16px" }}
                        />

                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                            <Checkbox
                                checked={question.allowMultiple || false}
                                onChange={(e) => onUpdate({ allowMultiple: e.target.checked })}
                                size="small"
                                sx={{
                                    color: "#57837E",
                                    "&.Mui-checked": { color: "#57837E" },
                                    padding: 0,
                                    width: "20px",
                                    height: "20px"
                                }}
                            />
                            <Typography sx={{ fontSize: "13px", color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                אפשר בחירה מרובה
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {question.options?.map((option, optionIndex) => (
                                <Box key={optionIndex} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <Box
                                        sx={{
                                            width: "20px",
                                            height: "20px",
                                            border: "2px solid #E2E8F0",
                                            borderRadius: question.allowMultiple ? "4px" : "50%",
                                            flexShrink: 0
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        value={option}
                                        onChange={(e) => handleUpdateOption(optionIndex, e.target.value)}
                                        placeholder={`אפשרות ${optionIndex + 1}`}
                                        variant="standard"
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: { fontSize: "14px", fontFamily: '"Heebo", "Roboto", sans-serif' }
                                        }}
                                    />
                                    {question.options.length > 2 && (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteOption(optionIndex)}
                                            sx={{ color: "#64748B", "&:hover": { color: "#EF4444" } }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            ))}
                            <Box
                                onClick={handleAddOption}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    cursor: "pointer",
                                    color: "#57837E",
                                    "&:hover": { color: "#46706B" }
                                }}
                            >
                                <AddIcon fontSize="small" />
                                <Typography sx={{ fontSize: "13px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                    הוסף אפשרות
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.LINK:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="קישור למידע נוסף"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                            sx={{ marginBottom: "16px" }}
                        />

                        <Typography sx={{ fontSize: "13px", color: "#64748B", marginBottom: "4px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                            כתובת URL
                        </Typography>
                        <TextField
                            fullWidth
                            value={question.linkUrl || ''}
                            onChange={(e) => onUpdate({ linkUrl: e.target.value })}
                            placeholder="https://example.com"
                            size="small"
                            dir="ltr"
                            sx={{
                                marginBottom: "16px",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />

                        <Typography sx={{ fontSize: "13px", color: "#64748B", marginBottom: "4px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                            הסבר נוסף (אופציונלי)
                        </Typography>
                        <TextField
                            fullWidth
                            value={question.linkDescription || ''}
                            onChange={(e) => onUpdate({ linkDescription: e.target.value })}
                            placeholder="תיאור קצר על הקישור..."
                            size="small"
                            multiline
                            minRows={2}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />

                        {/* Preview */}
                        <Box sx={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #E2E8F0" }}>
                            <Typography sx={{ fontSize: "12px", color: "#64748B", marginBottom: "8px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                תצוגה מקדימה:
                            </Typography>
                            <Box
                                sx={{
                                    backgroundColor: "#E8F5F3",
                                    borderRadius: "8px",
                                    padding: "16px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    gap: "6px",
                                    direction: "rtl"
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#57837E",
                                        fontWeight: 500,
                                        fontSize: "14px",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }}
                                >
                                    {question.question || 'קישור למידע נוסף'}
                                </Typography>
                                <span style={{ fontSize: "12px", color: "#57837E" }}>↗</span>
                            </Box>
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.YES_NO:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="טקסט השאלה"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                            sx={{ marginBottom: "16px" }}
                        />
                        <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!question.showDetailsOnYes}
                                        onChange={(e) => onUpdate({
                                            showDetailsOnYes: e.target.checked,
                                            showDetailsOnNo: e.target.checked ? false : question.showDetailsOnNo
                                        })}
                                        size="small"
                                        sx={{ color: "#57837E", "&.Mui-checked": { color: "#57837E" } }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: "13px", color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                        הצג שדה פירוט בבחירת "כן"
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!question.showDetailsOnNo}
                                        onChange={(e) => onUpdate({
                                            showDetailsOnNo: e.target.checked,
                                            showDetailsOnYes: e.target.checked ? false : question.showDetailsOnYes
                                        })}
                                        size="small"
                                        sx={{ color: "#57837E", "&.Mui-checked": { color: "#57837E" } }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: "13px", color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                        הצג שדה פירוט בבחירת "לא"
                                    </Typography>
                                }
                            />
                        </Box>
                        {!!question.showDetailsOnYes && (
                            <TextField
                                fullWidth
                                value={question.detailsPlaceholder || ''}
                                onChange={(e) => onUpdate({ detailsPlaceholder: e.target.value })}
                                placeholder="טקסט עזרה לשדה הפירוט לבחירת כן (placeholder)"
                                size="small"
                                sx={{
                                    marginTop: "12px",
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }
                                }}
                            />
                        )}
                        {!!question.showDetailsOnNo && (
                            <TextField
                                fullWidth
                                value={question.detailsPlaceholderNo || ''}
                                onChange={(e) => onUpdate({ detailsPlaceholderNo: e.target.value })}
                                placeholder="טקסט עזרה לשדה הפירוט בבחירת לא (placeholder)"
                                size="small"
                                sx={{
                                    marginTop: "12px",
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }
                                }}
                            />
                        )}
                        {/* Preview */}
                        <Box sx={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #E2E8F0" }}>
                            <Typography sx={{ fontSize: "12px", color: "#64748B", marginBottom: "8px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                תצוגה מקדימה:
                            </Typography>
                            <Box sx={{ display: "flex", gap: "8px" }}>
                                <Box sx={{ padding: "8px 24px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "14px" }}>כן</Box>
                                <Box sx={{ padding: "8px 24px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "14px" }}>לא</Box>
                            </Box>
                            {!!question.showDetailsOnYes && (
                                <TextField
                                    fullWidth
                                    disabled
                                    placeholder={question.detailsPlaceholder || "פירוט (אופציונלי)"}
                                    size="small"
                                    sx={{
                                        marginTop: "12px",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "8px",
                                            backgroundColor: "#F8FAFC",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        },
                                        "& .MuiInputBase-input::placeholder": {
                                            color: "#94A3B8",
                                            opacity: 1
                                        }
                                    }}
                                />
                            )}
                            {!!question.showDetailsOnNo && (
                                <TextField
                                    fullWidth
                                    disabled
                                    placeholder={question.detailsPlaceholderNo || "פירוט בבחירת לא (אופציונלי)"}
                                    size="small"
                                    sx={{
                                        marginTop: "12px",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "8px",
                                            backgroundColor: "#F8FAFC",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        },
                                        "& .MuiInputBase-input::placeholder": {
                                            color: "#94A3B8",
                                            opacity: 1
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.CONSENT_CHECKBOX:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="טקסט ההצהרה או התנאים..."
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />
                        {/* Preview */}
                        <Box sx={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #E2E8F0" }}>
                            <Typography sx={{ fontSize: "12px", color: "#64748B", marginBottom: "8px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                תצוגה מקדימה:
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px", border: "1px solid #E2E8F0", borderRadius: "8px" }}>
                                <Checkbox
                                    disabled
                                    size="small"
                                    sx={{
                                        padding: 0,
                                        "& .MuiSvgIcon-root": { fontSize: "20px" }
                                    }}
                                />
                                <Typography sx={{ fontSize: "13px", color: "#1E293B", fontFamily: '"Heebo", "Roboto", sans-serif', lineHeight: 1.4 }}>
                                    {question.question || 'טקסט ההצהרה'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.EMAIL:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="רשום את האימייל שלך"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                            sx={{ marginBottom: "16px" }}
                        />
                        <TextField
                            fullWidth
                            value={question.placeholder || ''}
                            onChange={(e) => onUpdate({ placeholder: e.target.value })}
                            placeholder="טקסט עזרה (placeholder)"
                            size="small"
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />
                        {/* Preview */}
                        <Box sx={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #E2E8F0" }}>
                            <Typography sx={{ fontSize: "12px", color: "#64748B", marginBottom: "8px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                תצוגה מקדימה:
                            </Typography>
                            <TextField
                                fullWidth
                                disabled
                                placeholder={question.placeholder || 'example@email.com'}
                                size="small"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        backgroundColor: "#F8FAFC",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    },
                                    "& .MuiInputBase-input::placeholder": {
                                        color: "#94A3B8",
                                        opacity: 1
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.PHONE:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="הוסף את הפלאפון שלך"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                            sx={{ marginBottom: "16px" }}
                        />
                        <TextField
                            fullWidth
                            value={question.placeholder || ''}
                            onChange={(e) => onUpdate({ placeholder: e.target.value })}
                            placeholder="טקסט עזרה (placeholder)"
                            size="small"
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />
                        {/* Preview */}
                        <Box sx={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #E2E8F0" }}>
                            <Typography sx={{ fontSize: "12px", color: "#64748B", marginBottom: "8px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                תצוגה מקדימה:
                            </Typography>
                            <TextField
                                fullWidth
                                disabled
                                placeholder={question.placeholder || '050-0000000'}
                                size="small"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        backgroundColor: "#F8FAFC",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    },
                                    "& .MuiInputBase-input::placeholder": {
                                        color: "#94A3B8",
                                        opacity: 1
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.RATING:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="בחר את הדירוג"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                            sx={{ marginBottom: "16px" }}
                        />
                        <Typography sx={{ fontSize: "13px", color: "#64748B", marginBottom: "4px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                            מספר כוכבים מקסימלי
                        </Typography>
                        <Select
                            value={Math.min(question.maxRating || 5, 5)}
                            onChange={(e) => onUpdate({ maxRating: e.target.value })}
                            size="small"
                            sx={{
                                minWidth: 100,
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {/* Star scale is capped at 5. */}
                            {[3, 4, 5].map(num => (
                                <MenuItem key={num} value={num}>{num}</MenuItem>
                            ))}
                        </Select>
                        {/* Preview */}
                        <Box sx={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #E2E8F0" }}>
                            <Typography sx={{ fontSize: "12px", color: "#64748B", marginBottom: "8px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                תצוגה מקדימה:
                            </Typography>
                            <Box sx={{ display: "flex", gap: "4px" }}>
                                {Array.from({ length: Math.min(question.maxRating || 5, 5) }, (_, i) => (
                                    <span key={i} style={{ fontSize: "24px", color: "#FFD700" }}>★</span>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.NUMBER:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="טקסט השאלה"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                            sx={{ marginBottom: "16px" }}
                        />
                        {/* Preview */}
                        <Box sx={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #E2E8F0" }}>
                            <Typography sx={{ fontSize: "12px", color: "#64748B", marginBottom: "8px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                תצוגה מקדימה:
                            </Typography>
                            <TextField
                                fullWidth
                                disabled
                                placeholder="0"
                                size="small"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        backgroundColor: "#F8FAFC",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    },
                                    "& .MuiInputBase-input::placeholder": {
                                        color: "#94A3B8",
                                        opacity: 1
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.DATE:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="בחירת תאריך"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                            sx={{ marginBottom: "16px" }}
                        />
                        {/* Preview */}
                        <Box sx={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #E2E8F0" }}>
                            <Typography sx={{ fontSize: "12px", color: "#64748B", marginBottom: "8px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                תצוגה מקדימה:
                            </Typography>
                            <DatePicker
                                value={question.previewDate ? dayjs(question.previewDate) : null}
                                // Store the picked day as a plain yyyy-MM-dd. toISOString()
                                // converts the picked LOCAL midnight to UTC, so in Israel
                                // (UTC+2/+3) picking 15/06 stored 1990-06-14T21:00Z — the day
                                // before the one clicked. Preview-only today, but this is the
                                // shape that gets copied into paths that DO reach the server.
                                onChange={(newValue) => onUpdate({ previewDate: newValue ? newValue.format('YYYY-MM-DD') : null })}
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        placeholder: "DD/MM/YYYY",
                                        sx: {
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px",
                                                backgroundColor: "#F8FAFC",
                                                fontFamily: '"Heebo", "Roboto", sans-serif'
                                            }
                                        }
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.FILE_UPLOAD:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="העלאת קובץ"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                            sx={{ marginBottom: "16px" }}
                        />
                        {/* Preview */}
                        <Box sx={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #E2E8F0" }}>
                            <Typography sx={{ fontSize: "12px", color: "#64748B", marginBottom: "8px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                תצוגה מקדימה:
                            </Typography>
                            {question.fileUrl ? (
                                <Box sx={{ position: "relative" }}>
                                    {question.fileType?.startsWith('image') ? (
                                        <img
                                            src={question.fileUrl}
                                            alt="Uploaded file"
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "200px",
                                                borderRadius: "8px",
                                                objectFit: "contain"
                                            }}
                                        />
                                    ) : (
                                        <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            padding: "16px",
                                            backgroundColor: "#F8FAFC",
                                            borderRadius: "8px",
                                            border: "1px solid #E2E8F0"
                                        }}>
                                            <FileIcon sx={{ color: "#57837E" }} />
                                            <Typography sx={{ fontSize: "14px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                                {question.fileName || 'קובץ שהועלה'}
                                            </Typography>
                                        </Box>
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={() => onUpdate({ fileUrl: null, fileName: null, fileType: null })}
                                        sx={{
                                            position: "absolute",
                                            top: -8,
                                            right: -8,
                                            backgroundColor: "#EF4444",
                                            color: "#FFFFFF",
                                            "&:hover": { backgroundColor: "#DC2626" }
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <label>
                                    <input
                                        type="file"
                                        accept="image/*,video/*,.pdf,.doc,.docx"
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    onUpdate({
                                                        fileUrl: reader.result,
                                                        fileName: file.name,
                                                        fileType: file.type
                                                    });
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            border: "2px dashed #E2E8F0",
                                            borderRadius: "8px",
                                            padding: "32px",
                                            textAlign: "center",
                                            backgroundColor: "#F8FAFC",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                borderColor: "#57837E",
                                                backgroundColor: "#E8F5F3"
                                            }
                                        }}
                                    >
                                        <UploadFileIcon sx={{ color: "#64748B", fontSize: "32px", marginBottom: "8px" }} />
                                        <Typography sx={{ color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                            לחץ או גרור קובץ לכאן
                                        </Typography>
                                        <Typography sx={{ color: "#94A3B8", fontSize: "12px", marginTop: "4px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                            תמונות, סרטונים, PDF, מסמכים
                                        </Typography>
                                    </Box>
                                </label>
                            )}
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.PDF_ATTACHMENT:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="קובץ PDF מוצמד"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                            sx={{ marginBottom: "16px" }}
                        />
                        {/* Upload Box */}
                        {question.pdfUrl ? (
                            <Box sx={{ position: "relative" }}>
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    padding: "16px",
                                    backgroundColor: "#FEF2F2",
                                    borderRadius: "8px",
                                    border: "1px solid #FECACA"
                                }}>
                                    <PdfIcon sx={{ color: "#DC2626", fontSize: "32px" }} />
                                    <Box>
                                        <Typography sx={{ fontSize: "14px", fontWeight: 600, fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                            {question.pdfName || 'קובץ PDF'}
                                        </Typography>
                                        <Typography sx={{ fontSize: "12px", color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                            קובץ PDF מוצמד
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => onUpdate({ pdfUrl: null, pdfName: null })}
                                    sx={{
                                        position: "absolute",
                                        top: -8,
                                        right: -8,
                                        backgroundColor: "#EF4444",
                                        color: "#FFFFFF",
                                        "&:hover": { backgroundColor: "#DC2626" }
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ) : isUploading ? (
                            <Box sx={{
                                border: "2px dashed #57837E",
                                borderRadius: "8px",
                                padding: "32px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "12px",
                                backgroundColor: "#F8FAFC"
                            }}>
                                <CircularProgress size={32} sx={{ color: "#57837E" }} />
                                <Typography sx={{ fontSize: "14px", color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                    מעלה קובץ... {uploadProgress}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={uploadProgress}
                                    sx={{
                                        width: "100%",
                                        maxWidth: "200px",
                                        height: "6px",
                                        borderRadius: "3px",
                                        backgroundColor: "#E2E8F0",
                                        "& .MuiLinearProgress-bar": { backgroundColor: "#57837E" }
                                    }}
                                />
                            </Box>
                        ) : (
                            <label>
                                <input
                                    type="file"
                                    accept=".pdf"
                                    style={{ display: "none" }}
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            setIsUploading(true);
                                            setUploadProgress(0);
                                            try {
                                                const cdnUrl = await uploadPdf(file, (progress) => {
                                                    setUploadProgress(progress);
                                                });
                                                onUpdate({
                                                    pdfUrl: cdnUrl,
                                                    pdfName: file.name
                                                });
                                            } catch (error) {
                                                console.error('Error uploading PDF:', error);
                                                alert('שגיאה בהעלאת הקובץ');
                                            } finally {
                                                setIsUploading(false);
                                                setUploadProgress(0);
                                            }
                                        }
                                    }}
                                />
                                <Box
                                    sx={{
                                        border: "2px dashed #E2E8F0",
                                        borderRadius: "8px",
                                        padding: "32px",
                                        textAlign: "center",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            borderColor: "#57837E",
                                            backgroundColor: "#F8FAFC"
                                        }
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                                        <PdfIcon sx={{ color: "#57837E", fontSize: "24px" }} />
                                        <Typography sx={{ color: "#57837E", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                            העלה קובץ PDF
                                        </Typography>
                                    </Box>
                                </Box>
                            </label>
                        )}
                    </Box>
                );

            case QUESTION_TYPE_KEYS.SIGNATURE:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="חתימה דיגיטלית"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />
                        <Typography sx={{ fontSize: "12px", color: "#94A3B8", marginTop: "8px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                            החתימה תתבצע באפליקציית המובייל
                        </Typography>
                    </Box>
                );

            // Default for simple question types
            default:
                return (
                    <Box>
                        <TextField
                            fullWidth
                            value={question.question || ''}
                            onChange={(e) => onUpdate({ question: e.target.value })}
                            placeholder="טקסט השאלה"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                            sx={{ marginBottom: "16px" }}
                        />
                        {(question.type === QUESTION_TYPE_KEYS.SHORT_TEXT || question.type === QUESTION_TYPE_KEYS.LONG_TEXT) && (
                            <>
                                <TextField
                                    fullWidth
                                    value={question.placeholder || ''}
                                    onChange={(e) => onUpdate({ placeholder: e.target.value })}
                                    placeholder="טקסט עזרה (placeholder)"
                                    size="small"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "8px",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }
                                    }}
                                />
                                {/* Preview */}
                                <Box sx={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #E2E8F0" }}>
                                    <Typography sx={{ fontSize: "12px", color: "#64748B", marginBottom: "8px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                        תצוגה מקדימה:
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        disabled
                                        multiline={question.type === QUESTION_TYPE_KEYS.LONG_TEXT}
                                        minRows={question.type === QUESTION_TYPE_KEYS.LONG_TEXT ? 3 : 1}
                                        placeholder={question.placeholder || (question.type === QUESTION_TYPE_KEYS.SHORT_TEXT ? 'תשובה קצרה...' : 'תשובה מפורטת...')}
                                        size="small"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px",
                                                backgroundColor: "#F8FAFC",
                                                fontFamily: '"Heebo", "Roboto", sans-serif'
                                            },
                                            "& .MuiInputBase-input::placeholder": {
                                                color: "#94A3B8",
                                                opacity: 1
                                            }
                                        }}
                                    />
                                </Box>
                            </>
                        )}
                    </Box>
                );
        }
    };

    // Simple card for header types
    if (isHeaderType) {
        return (
            <Paper
                ref={cardRef}
                elevation={0}
                sx={{
                    backgroundColor: hasError ? "#FEF2F2" : "#FFFFFF",
                    border: hasError ? "2px solid #EF4444" : "1px solid #E2E8F0",
                    borderRadius: "12px",
                    padding: "16px",
                    direction: "rtl",
                    transition: "all 0.2s ease",
                    "&:hover": {
                        borderColor: hasError ? "#DC2626" : "#57837E"
                    }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                    <Box
                        draggable
                        onDragStart={handleDragStartWithImage}
                        onDragEnd={onDragEnd}
                        sx={{ cursor: "grab", "&:active": { cursor: "grabbing" }, display: "flex", alignItems: "center", marginTop: "4px" }}
                    >
                        <DragIcon sx={{ color: "#94A3B8" }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        {renderQuestionEditor()}
                    </Box>
                    <Box sx={{ display: "flex", gap: "4px" }}>
                        <IconButton size="small" onClick={onDuplicate} sx={{ color: "#64748B", "&:hover": { color: "#1E293B" } }}>
                            <CopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={onDelete} sx={{ color: "#64748B", "&:hover": { color: "#EF4444" } }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </Paper>
        );
    }

    // Full card for question types
    return (
        <Paper
            ref={cardRef}
            elevation={0}
            sx={{
                backgroundColor: hasError ? "#FEF2F2" : "#FFFFFF",
                border: hasError ? "2px solid #EF4444" : "1px solid #E2E8F0",
                borderRadius: "12px",
                direction: "rtl",
                transition: "all 0.2s ease",
                "&:hover": {
                    borderColor: hasError ? "#DC2626" : "#57837E"
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 16px",
                    borderBottom: "1px solid #E2E8F0"
                }}
            >
                <Box
                    draggable
                    onDragStart={handleDragStartWithImage}
                    onDragEnd={onDragEnd}
                    sx={{ cursor: "grab", "&:active": { cursor: "grabbing" }, display: "flex", alignItems: "center" }}
                >
                    <DragIcon sx={{ color: "#94A3B8" }} />
                </Box>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                        value={question.type}
                        onChange={(e) => onUpdate({ type: e.target.value })}
                        sx={{
                            fontSize: "13px",
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        {Object.entries(QUESTION_TYPES).map(([key, value]) => (
                            <MenuItem key={key} value={key} sx={{ fontSize: "13px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                {value.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ flex: 1 }} />
                {question.type !== QUESTION_TYPE_KEYS.PDF_ATTACHMENT &&
                    question.type !== QUESTION_TYPE_KEYS.SIGNATURE && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={question.required || false}
                                    onChange={(e) => onUpdate({ required: e.target.checked })}
                                    size="small"
                                    sx={{ color: "#57837E", "&.Mui-checked": { color: "#57837E" } }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: "13px", color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                    שדה חובה
                                </Typography>
                            }
                        />
                    )}
                <IconButton size="small" onClick={onDuplicate} sx={{ color: "#64748B", "&:hover": { color: "#1E293B" } }}>
                    <CopyIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={onDelete} sx={{ color: "#64748B", "&:hover": { color: "#EF4444" } }}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)} sx={{ color: "#64748B" }}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            {/* Body */}
            <Collapse in={isExpanded}>
                <Box sx={{ padding: "16px" }}>
                    {renderQuestionEditor()}
                    {/* Validation Error Message */}
                    {hasError && (
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginTop: "12px",
                            padding: "8px 12px",
                            backgroundColor: "#FEF2F2",
                            border: "1px solid #FECACA",
                            borderRadius: "8px"
                        }}>
                            <ErrorIcon sx={{ color: "#EF4444", fontSize: "18px" }} />
                            <Typography sx={{
                                fontSize: "13px",
                                color: "#DC2626",
                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                fontWeight: 500
                            }}>
                                {getValidationErrorMessage(question.type)}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default QuestionCard;
